import { useEffect, useMemo, useState } from 'react'
import AddRoundedIcon from '@mui/icons-material/AddRounded'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import Inventory2RoundedIcon from '@mui/icons-material/Inventory2Rounded'
import { useWorkspace } from '../../context/useWorkspace'
import { roleCanPerform } from '../../utils/workspacePermissions'
import { useNls } from '../../nls/nlsProvider'
import { subscribeWorkspaceStock } from '../../services/stockService'
import type { StockItem, StockRequirement } from '../../types/stock'
import { normalizeStockRequirements, stockRequirementIds } from '../../services/resourceAdapter'
import { AppDialog } from '../Common/AppDialog'
import { Button } from '../Common/Button'
import { Card } from '../Common/Card'
import { Checkbox } from '../Common/Checkbox'
import { IconButton } from '../Common/IconButton'
import { RailChipTabs } from '../Common/RailChipTabs'
import { TextInput } from '../Common/TextInput'
import { WorkspaceItemPicker } from '../Common/WorkspaceItemPicker'

type Props = {
  workspaceId: string
  requiredStockIds: string[] | undefined
  stockRequirements?: StockRequirement[] | undefined
  consumptionMode: 'reserve' | 'consume' | undefined
  disabled?: boolean
  onChange: (next: { requiredStockIds: string[]; stockRequirements: StockRequirement[]; consumptionMode: 'reserve' | 'consume' }) => void
}

export const NodeStockTab = ({ workspaceId, requiredStockIds, stockRequirements, consumptionMode, disabled = false, onChange }: Props) => {
  const { activeRole } = useWorkspace()
  const { t } = useNls()
  // Testing mode: enablement gate bypassed; mutation still gated by role.
  const canEditLinks = roleCanPerform(activeRole, 'EDITOR') && !disabled

  const [allItems, setAllItems] = useState<StockItem[]>([])
  const [isPickerOpen, setIsPickerOpen] = useState(false)

  useEffect(() => {
    if (!workspaceId) return
    return subscribeWorkspaceStock(workspaceId, setAllItems)
  }, [workspaceId])

  const requirements = useMemo(
    () => normalizeStockRequirements({ requiredStockIds, stockRequirements }),
    [requiredStockIds, stockRequirements],
  )
  const selectedIds = useMemo(() => stockRequirementIds(requirements), [requirements])
  const mode = consumptionMode ?? 'reserve'

  const byId = useMemo(() => new Map(allItems.map((item) => [item.id, item])), [allItems])

  const commitRequirements = (nextRequirements: StockRequirement[], nextMode = mode) =>
    onChange({ requiredStockIds: stockRequirementIds(nextRequirements), stockRequirements: nextRequirements, consumptionMode: nextMode })
  const updateMode = (nextMode: 'reserve' | 'consume') => commitRequirements(requirements, nextMode)

  return (
    <div className="stockTab">
      <div data-tour-id="node-stock-mode">
        <RailChipTabs<'reserve' | 'consume'>
          orientation="horizontal"
          ariaLabel={t('NLSResourcesStockConsumptionModeAria')}
          value={mode}
          onChange={(key) => updateMode(key)}
          items={[
            { key: 'reserve', label: t('NLSResourcesStockReserveWhileActive'), disabled: !canEditLinks },
            { key: 'consume', label: t('NLSResourcesStockConsumeOnCompletion'), disabled: !canEditLinks },
          ]}
        />
      </div>

      <div className="stockTab__list">
        {requirements.length === 0 && <div className="stockTab__empty">{t('NLSResourcesStockNoRequirements')}</div>}
        {requirements.map((requirement) => {
          const id = requirement.stockItemId
          const item = byId.get(id)
          if (!item) {
            return (
              <Card
                key={id}
                variant="outline"
                className="stockTab__card stockTab__card--missing"
                title={t('NLSResourcesStockDeletedReferenceTitle')}
                subtitle={`id: ${id}`}
                actions={
                  canEditLinks && (
                    <IconButton
                      icon={<CloseRoundedIcon fontSize="small" />}
                      label={t('NLSResourcesStockRemoveDeletedReferenceAria')}
                      size="sm"
                      variant="tertiary"
                      onClick={() => commitRequirements(requirements.filter((entry) => entry.stockItemId !== id))}
                    />
                  )
                }
              />
            )
          }
          const available = Math.max(0, item.quantity - item.reservedQuantity)
          return (
            <Card
              key={id}
              variant="glass"
              className="stockTab__card"
              icon={<Inventory2RoundedIcon fontSize="inherit" />}
              title={item.name}
              subtitle={t('NLSResourcesStockRequirementDetail', {
                available,
                quantity: requirement.quantity,
                unit: requirement.unitOverride || item.unit || 'unit',
                location: item.location || t('NLSResourcesStockNoLocation'),
              })}
              actions={
                canEditLinks && (
                  <IconButton
                    icon={<CloseRoundedIcon fontSize="small" />}
                    label={t('NLSResourcesStockRemoveItemAria', { name: item.name })}
                    size="sm"
                    variant="tertiary"
                    onClick={() => commitRequirements(requirements.filter((entry) => entry.stockItemId !== id))}
                  />
                )
              }
            >
              {canEditLinks && (
                <div className="stockTab__requirementControls">
                  <label className="stockTab__controlField stockTab__controlField--qty">
                    <span className="stockTab__controlLabel">{t('NLSResourcesStockQty')}</span>
                    <TextInput
                      size="sm"
                      type="number"
                      min={1}
                      value={String(requirement.quantity)}
                      onChange={(next) => commitRequirements(requirements.map((entry) => entry.stockItemId === id ? { ...entry, quantity: Math.max(1, Math.floor(Number(next) || 1)) } : entry))}
                    />
                  </label>
                  <label className="stockTab__controlField stockTab__controlField--unit">
                    <span className="stockTab__controlLabel">{t('NLSResourcesStockFieldUnit')}</span>
                    <TextInput
                      size="sm"
                      type="text"
                      value={requirement.unitOverride ?? ''}
                      placeholder={item.unit || 'unit'}
                      onChange={(next) => commitRequirements(requirements.map((entry) => entry.stockItemId === id ? { ...entry, unitOverride: next.trim() || null } : entry))}
                    />
                  </label>
                  <div className="stockTab__controlField stockTab__controlField--checkbox">
                    <Checkbox
                      size="sm"
                      label={t('NLSResourcesStockRequired')}
                      checked={requirement.required !== false}
                      onChange={(checked) => commitRequirements(requirements.map((entry) => entry.stockItemId === id ? { ...entry, required: checked } : entry))}
                    />
                  </div>
                </div>
              )}
            </Card>
          )
        })}
      </div>

      {canEditLinks && (
        <div className="stockTab__picker">
          <Button
            type="button"
            variant="secondary"
            size="sm"
            icon={<AddRoundedIcon fontSize="small" />}
            onClick={() => setIsPickerOpen(true)}
            data-tour-id="node-stock-add"
          >
            Add stock item…
          </Button>
        </div>
      )}

      {isPickerOpen && (
        <AppDialog
          open={isPickerOpen}
          size="attachPicker"
          title="Add stock item"
          onDismiss={() => setIsPickerOpen(false)}
          actions={[]}
          message={
            <WorkspaceItemPicker
              workspaceId={workspaceId}
              enabledKinds={['stock']}
              stock={allItems}
              selectedIds={new Set(selectedIds)}
              onSelect={(ref) => {
                commitRequirements([
                  ...requirements,
                  { stockItemId: ref.id, quantity: 1, unitOverride: null, required: true },
                ])
                setIsPickerOpen(false)
              }}
              onDetach={async (_kind, stockItemId) => {
                commitRequirements(requirements.filter((entry) => entry.stockItemId !== stockItemId))
              }}
              onClose={() => setIsPickerOpen(false)}
            />
          }
        />
      )}
    </div>
  )
}
